import React from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  List,
  ListItem,
  Chip,
  //   Stack,
  Grid,
  useTheme
} from '@mui/material';
import {
  Lightbulb,
  //   Psychology,
  Recommend,
  TipsAndUpdates,
  FormatQuote,
  CheckCircleOutline
} from '@mui/icons-material';
import { motion } from 'framer-motion';
// import { FbF } from './FbF';

const FieldInsightsCard = ({ fieldKey }) => {
  const theme = useTheme();
  const FbF = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults?.field_by_field_analysis?.analysis || null);
  const mainCompanyName = FbF?.my_company || 'My Company';

  // Add comprehensive data validation
  if (!FbF || !FbF.field_analysis || !fieldKey) {
    return (
      <Card sx={{ mt: 3, p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No field analysis data available
        </Typography>
      </Card>
    );
  }

  // Check if the field exists in FbF data
  if (!FbF.field_analysis[fieldKey]) {
    return (
      <Card sx={{ mt: 3, p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No analysis available for this field
        </Typography>
      </Card>
    );
  }

  const fieldData = FbF.field_analysis[fieldKey];

  // Create sections based on available data
  const sections = [
    {
      title: mainCompanyName,
      icon: <CheckCircleOutline />,
      data: fieldData.my_company,
      render: (data) => renderMyCompanySection(data, fieldKey),
      gridSize: { xs: 12, md: 6 }, // Full width for My Company section
      gradient: 'primary',
      titleGradient: 'linear-gradient(135deg, #3f51b5 0%, #2196F3 60%, #03A9F4 100%)'
    },
    {
      title: 'Competitor Highlights',
      icon: <FormatQuote />,
      data: fieldData.competitors_highlights,
      render: (data) => renderListItems(data),
      gridSize: { xs: 12, md: 6 }, // 2 columns for medium screens and up
      gradient: 'secondary',
      titleGradient: 'linear-gradient(135deg, #9c27b0 0%, #673AB7 60%, #3F51B5 100%)'
    },
    {
      title: 'Insights',
      icon: <Lightbulb />,
      data: fieldData.insights,
      render: (data) => renderListItems(data),
      gridSize: { xs: 12, md: 6 }, // 2 columns for medium screens and up
      gradient: 'warning',
      titleGradient: 'linear-gradient(135deg, #ff9800 0%, #ff5722 60%, #f44336 100%)'
    },
    {
      title: 'Recommendations',
      icon: <Recommend />,
      data: fieldData.recommendations,
      render: (data) => renderListItems(data),
      gridSize: { xs: 12, md: 6 }, // 2 columns for medium screens and up
      gradient: 'success',
      titleGradient: 'linear-gradient(135deg, #4caf50 0%, #009688 60%, #00bcd4 100%)'
    }
  ];

  // Get gradient based on type
  const getGradient = (type) => {
    switch (type) {
      case 'warning':
        return 'linear-gradient(135deg, rgba(255,167,38,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)';
      case 'error':
        return 'linear-gradient(135deg, rgba(255,82,82,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)';
      case 'success':
        return 'linear-gradient(135deg, rgba(76,175,80,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)';
      case 'secondary':
        return 'linear-gradient(135deg, rgba(156,39,176,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)';
      default: // primary
        return 'linear-gradient(135deg, rgba(63,81,181,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)';
    }
  };

  // Get icon color based on type
  const getIconColor = (type) => {
    switch (type) {
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success.main;
      case 'secondary':
        return theme.palette.secondary.main;
      default:
        return theme.palette.primary.main;
    }
  };

  // Render the My Company section based on field key
  const renderMyCompanySection = (data) => {
    // Return null if no data
    if (!data) return null;

    const renderValue = (key, value) => {
      if (!value) return null;

      // For arrays, render as list items
      if (Array.isArray(value)) {
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {formatLabel(key)}:
            </Typography>
            <List dense disablePadding sx={{ pl: 2 }}>
              {value.map((item, idx) => (
                <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '6px',
                        height: '6px',
                        bgcolor: key.includes('weakness') ? 'error.main' : 'success.main',
                        borderRadius: '50%',
                        flexShrink: 0,
                        mt: 1
                      }}
                    />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        );
      }

      // For strings
      return (
        <Box key={key} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            {formatLabel(key)}:
          </Typography>
          <Typography variant="body1" sx={{ pl: 2 }}>
            {value}
          </Typography>
        </Box>
      );
    };

    return <Box sx={{ mt: 1 }}>{Object.entries(data).map(([key, value]) => renderValue(key, value))}</Box>;
  };

  // Render list items
  const renderListItems = (items) => {
    if (!items || !Array.isArray(items) || items.length === 0) return null;

    return (
      <List dense disablePadding sx={{ mt: 1 }}>
        {items.map((item, idx) => (
          <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: '6px',
                  height: '6px',
                  bgcolor: 'primary.main',
                  borderRadius: '50%',
                  flexShrink: 0,
                  mt: 1
                }}
              />
              <Typography variant="body1">{item}</Typography>
            </Box>
          </ListItem>
        ))}
      </List>
    );
  };

  // Format label to be more readable
  const formatLabel = (str) => {
    return str
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Card
        sx={{
          mt: 3,
          borderRadius: 2,
          position: 'relative',
          isolation: 'isolate',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            padding: '1px',
            borderRadius: 'inherit',
            background: 'linear-gradient(135deg, rgba(63,81,181,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            zIndex: -1
          },
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TipsAndUpdates sx={{ color: 'primary.main', mr: 1 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #3f51b5 0%, #673ab7 60%, rgba(103,58,183,0.7) 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent'
              }}
            >
              Field Insights: {formatLabel(fieldKey)}
            </Typography>
          </Box>

          <Chip label="Powered by Askpot AI" size="small" color="primary" variant="outlined" sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            {sections.map((section, index) => {
              if (!section.data) return null;

              return (
                <Grid item key={`${section.title}-${index}`} {...section.gridSize}>
                  {index > 0 && <Divider sx={{ mb: 2, display: { xs: 'block', md: 'none' } }} />}

                  <motion.div whileHover={{ scale: 1.015 }} transition={{ duration: 0.2 }}>
                    <Box
                      sx={{
                        p: 2.5,
                        height: '100%',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        position: 'relative',
                        isolation: 'isolate',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          padding: '1px',
                          borderRadius: 'inherit',
                          background: getGradient(section.gradient),
                          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                          WebkitMaskComposite: 'xor',
                          maskComposite: 'exclude',
                          zIndex: -1
                        },
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        {React.cloneElement(section.icon, {
                          style: {
                            color: getIconColor(section.gradient),
                            marginRight: '8px',
                            fontSize: '24px'
                          }
                        })}
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            background: section.titleGradient,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            color: 'transparent'
                          }}
                        >
                          {section.title}
                        </Typography>
                      </Box>

                      {section.render(section.data)}
                    </Box>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default FieldInsightsCard;
